import type {
  Account,
  Insight,
} from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import type { ReactNode } from "react"
import {
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from "react"

import { CalculationsContext } from "./CalculationsContext"
import type { CalculationsContextValue } from "./CalculationsContext"
import { InsightCalculationWorkerComponent } from "./InsightCalculationWorkerComponent"
import { useCalculatorGroupsState } from "./useCalculatorGroupsState"

import {
  UserEventsLoadedComponent,
  UserEntriesLoadedComponent,
} from "src/components"
import { useFollowedUsers } from "src/contexts"
import {
  useThisMinute,
  useStateObject,
} from "src/hooks"
import { InsightCalculatorGroup } from "src/types"
import { sleepSettingToTime } from "src/utils"
import type {
  CalculationWorkerValueData,
  QuartileCalculationValue,
  EmptyQuartileCalculationValue,
} from "src/workers/insightCalculationWorker"

type AccountIdToType<T> = Record<Account["id"], T>

interface Props {
  insights: Array<Insight>;
  children: ReactNode;
}

export function CalculationsContextProvider(props: Props) {
  const { insights } = props

  const {
    currentFollow,
    currentUserId,
  } = useFollowedUsers()
  const wakeUp = currentFollow.sleep_settings.end_time

  const queryEndTime = useThisMinute()

  const timeSinceWakeUpInHours = useMemo(
    () => {
      const diff = dayjs(queryEndTime).diff(
        sleepSettingToTime(wakeUp),
        "hour",
        true,
      )

      return (diff + 24) % 24
    },
    [
      queryEndTime,
      wakeUp,
    ],
  )

  const [
    exerciseCalculations,
    addExerciseCalculations,
  ] = useStateObject<
    AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.Exercise]>
  >({})
  const [
    foodCalculations,
    addFoodCalculations,
  ] = useStateObject<
    AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.Food]>
  >({})
  const [
    glucoseCalculations,
    addGlucoseCalculations,
  ] = useStateObject<
    AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.Glucose]>
  >({})
  const [
    insulinCalculations,
    addInsulinCalculations,
  ] = useStateObject<
    AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.Insulin]>
  >({})
  const [
    liquidCalculations,
    addLiquidCalculations,
  ] = useStateObject<
    AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.Liquid]>
  >({})
  const [
    quartileCalculations,
    setQuartileCalculations,
  ] = useState<
    AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.Quartile]>
  >({})

  const quartileCalculationsRef =
    useRef<
      AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.Quartile]>
    >(quartileCalculations)

  const addQuartileCalculation = useCallback(
    (
      accountId: Account["id"],
      value: EmptyQuartileCalculationValue | QuartileCalculationValue,
    ) => {
      const accountValues = { ...quartileCalculationsRef.current[accountId] }

      if (value[1]) {
        accountValues[value[0]] = value[1]
      } else {
        delete accountValues[value[0]]
      }

      quartileCalculationsRef.current = {
        ...quartileCalculationsRef.current,
        [accountId]: accountValues,
      }

      setQuartileCalculations(quartileCalculationsRef.current)
    },
    [
      quartileCalculationsRef,
      setQuartileCalculations,
    ],
  )

  const [
    timeInRangeCalculation,
    addTimeInRangeCalculation,
  ] = useStateObject<AccountIdToType<CalculationsContextValue[InsightCalculatorGroup.TimeInRange]>>({})

  const calculatorGroupsState = useCalculatorGroupsState(insights)

  const [
    worker,
    setWorker,
  ] = useState<Worker | null>(null)

  const value = useMemo(
    () => ({
      queryEndTime,
      timeSinceWakeUpInHours,
      exerciseCalculations: exerciseCalculations[currentUserId ?? -1] ?? {},
      foodCalculations: foodCalculations[currentUserId ?? -1] ?? {},
      glucoseCalculations: glucoseCalculations[currentUserId ?? -1] ?? {},
      insulinCalculations: insulinCalculations[currentUserId ?? -1] ?? {},
      liquidCalculations: liquidCalculations[currentUserId ?? -1] ?? {},
      quartileCalculations: quartileCalculations[currentUserId ?? -1] ?? {},
      timeInRangeCalculation: timeInRangeCalculation[currentUserId ?? -1] ?? undefined,
    }),
    [
      queryEndTime,
      timeSinceWakeUpInHours,
      currentUserId,
      exerciseCalculations,
      foodCalculations,
      glucoseCalculations,
      insulinCalculations,
      liquidCalculations,
      quartileCalculations,
      timeInRangeCalculation,
    ],
  )

  const onMessage = useCallback(
    (message: MessageEvent<CalculationWorkerValueData>) => {
      switch (message.data.calculationType) {
        case InsightCalculatorGroup.Glucose:
          addGlucoseCalculations({
            [message.data.accountId]: message.data.value,
          })
          break
        case InsightCalculatorGroup.Exercise:
          addExerciseCalculations({
            [message.data.accountId]: message.data.value,
          })
          break
        case InsightCalculatorGroup.Food:
          addFoodCalculations({
            [message.data.accountId]: message.data.value,
          })
          break
        case InsightCalculatorGroup.Insulin:
          addInsulinCalculations({
            [message.data.accountId]: message.data.value,
          })
          break
        case InsightCalculatorGroup.Liquid:
          addLiquidCalculations({
            [message.data.accountId]: message.data.value,
          })
          break
        case InsightCalculatorGroup.Quartile:
          addQuartileCalculation(
            message.data.accountId,
            message.data.value,
          )
          break
        case InsightCalculatorGroup.TimeInRange:
          addTimeInRangeCalculation({
            [message.data.accountId]: message.data.value,
          })
          break
      }
    },
    [
      addExerciseCalculations,
      addFoodCalculations,
      addGlucoseCalculations,
      addInsulinCalculations,
      addLiquidCalculations,
      addTimeInRangeCalculation,
      addQuartileCalculation,
    ],
  )

  useEffect(
    () => {
      const newWorker = new Worker(
        new URL(
          "src/workers/insightCalculationWorker/insightCalculationWorker.ts",
          import.meta.url,
        ),
      )

      setWorker(newWorker)

      newWorker.addEventListener(
        "message",
        onMessage,
      )

      return () => {
        newWorker.removeEventListener(
          "message",
          onMessage,
        )
        newWorker.terminate()
      }
    },
    [
      setWorker,
      onMessage,
    ],
  )

  return (
    <CalculationsContext.Provider value={value}>
      {props.children}
      <UserEventsLoadedComponent>
        <UserEntriesLoadedComponent>
          {timeSinceWakeUpInHours && worker && (
            <InsightCalculationWorkerComponent
              calculatorGroupsState={calculatorGroupsState}
              queryEndTime={queryEndTime}
              timeSinceWakeUpInHours={timeSinceWakeUpInHours}
              worker={worker}
            />
          )}
        </UserEntriesLoadedComponent>
      </UserEventsLoadedComponent>
    </CalculationsContext.Provider>
  )
}
